import React, { useState, useEffect } from "react";
import { Row } from "react-bootstrap";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    Legend,
    ResponsiveContainer,
    ReferenceArea,
    ReferenceLine
} from 'recharts';
import { validateToken, callApi } from "../../../../../lib/utils";
import { PulseLoader } from 'react-spinners';
import "../../../../PatientEventDrillDown/HRM/HRM.css";

const formatEventDuration = (totalSeconds) => {
    if (totalSeconds >= 60) {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds - minutes * 60;
        return `${minutes} min ${seconds} secs`;
    }
    return `${totalSeconds} seconds`;
};

const formatModalTitle = (totalSeconds) =>
    `Heart Rate Monitoring ( Duration : ${formatEventDuration(totalSeconds)} )`;

const useViewportWidth = () => {
    const [width, setWidth] = useState(
        typeof window !== "undefined" ? window.innerWidth : 1024
    );

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return width;
};

const HRMEventModal = ({ modalData, hrmEvent }) => {

    const [graphMomdalData, setGraphModalData] = useState({});
    const [loading, setLoading] = useState(false);
    const viewportWidth = useViewportWidth();
    const isCompactLabels = viewportWidth < 768;
    const isMobileView = viewportWidth < 768;

    useEffect(() => {
        setLoading(true);
        validateToken((err) => {
            if (err) {
                setLoading(false);
                window.location = "/logout";
                return;
            }
            callApi(
                `/api/event/hrm/sensor-data/${hrmEvent.id}`,
                {},
                "GET"
            )
                .then((response) => {
                    setGraphModalData(response?.data?.data);
                    setLoading(false);
                })
                .catch((err) => {
                    console.log("err: ", err);
                    setLoading(false);
                });
        });
    }, []);

    const NewHeartRateChart = (props) => {
        const result = props.graphData?.rawHeartRateData?.filter((item) => {
            return item.value !== 0;
        });

        let lowThreshold = hrmEvent?.hrmSensorSetting?.lowHRThreshold;
        let highThreshold = hrmEvent?.hrmSensorSetting?.highHRThreshold;

        const formatTime = (timestampMs) => {
            if (!timestampMs) return '';
            const date = new Date(timestampMs);
            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            const formattedHours = String(hours).padStart(2, '0');
            return `${formattedHours}:${minutes} ${ampm}`;
        };

        let chartData = [];
        if (result !== undefined) {
            chartData = [...result];
        }

        let eventStartTime = (hrmEvent?.eventTimestamp - (hrmEvent?.totalEventDuration * 1000));
        let eventEndTime = hrmEvent?.eventTimestamp;

        let dataMin = chartData.length > 0 ? chartData[0].timestamp : null;
        let dataMax = chartData.length > 0 ? chartData[chartData.length - 1].timestamp : null;

        // Task `modalData` does not include HRM subtype/settings; those are on `hrmEvent`.
        // HTE (high) uses highHRThresholdDuration; otherwise use lowHRThresholdDuration (minutes)
        const elevatedHRDurationMinutes =
          hrmEvent?.eventSubType === "HTE"
            ? hrmEvent?.hrmSensorSetting?.highHRThresholdDuration
            : hrmEvent?.hrmSensorSetting?.lowHRThresholdDuration;
        const durationMs = Number(elevatedHRDurationMinutes) * 60 * 1000;
        let calculatedElevatedHR = Number.isFinite(durationMs)
          ? eventStartTime - durationMs
          : eventStartTime;
        let elevatedHR = calculatedElevatedHR;

        if (dataMin && dataMin > calculatedElevatedHR) {
            elevatedHR = dataMin;
        }

        const allValidTimestamps = [elevatedHR, eventStartTime, eventEndTime, dataMin, dataMax].filter(
            (ts) => typeof ts === 'number' && !isNaN(ts)
        );

        let finalDomainMin = 'dataMin';
        let finalDomainMax = 'dataMax';

        if (allValidTimestamps.length > 0) {
            const minTimestamp = Math.min(...allValidTimestamps);
            const maxTimestamp = Math.max(...allValidTimestamps);

            finalDomainMin = minTimestamp - 60000;
            finalDomainMax = maxTimestamp + 60000;

        } else if (chartData.length > 0) {
            finalDomainMin = dataMin - 60000;
            finalDomainMax = dataMax + 60000;
        }

        const generateTicks = (minDomain, maxDomain) => {
            if (typeof minDomain !== 'number' || typeof maxDomain !== 'number' || isNaN(minDomain) || isNaN(maxDomain)) {
                return [];
            }
            const ticks = [];
            let currentTick = new Date(minDomain);
            currentTick.setSeconds(0, 0);

            if (currentTick.getTime() < minDomain) {
                currentTick.setMinutes(currentTick.getMinutes() + 1);
            }

            while (currentTick.getTime() <= maxDomain) {
                ticks.push(currentTick.getTime());
                currentTick.setMinutes(currentTick.getMinutes() + 1);
            }
            return ticks;
        };

        const customTicks = generateTicks(finalDomainMin, finalDomainMax);
        const chartHeight =
            viewportWidth >= 768 ? 400
            : viewportWidth >= 576 ? 280
            : 260;
        const chartMargin = isMobileView
            ? { top: 8, right: 22, left: 10, bottom: 8 }
            : { top: 30, right: 30, left: 20, bottom: 5 };

        const referenceLineLabel = (value, fill, { dy = -5, dx = 0, position = 'top' } = {}) => ({
            value,
            position,
            fill,
            fontSize: 12,
            dy,
            dx,
        });

        const verticalReferenceLabel = (text, fill, side, { verticalRatio = 0.14 } = {}) => (labelProps) => {
            const viewBox = labelProps?.viewBox;
            const lineX = viewBox?.x;
            const lineY = viewBox?.y;
            const lineHeight = viewBox?.height;
            if (typeof lineX !== 'number' || typeof lineY !== 'number' || typeof lineHeight !== 'number') {
                return null;
            }

            const xOffset = side === 'before' ? -10 : 10;
            const anchorX = lineX + xOffset;
            const anchorY = lineY + Math.min(lineHeight * verticalRatio, 80);
            const textAnchor = side === 'before' ? 'end' : 'start';

            return (
                <text
                    x={anchorX}
                    y={anchorY}
                    fill={fill}
                    fontSize={8}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    transform={`rotate(-90, ${anchorX}, ${anchorY})`}
                >
                    {text}
                </text>
            );
        };

        const elevatedLabel = isCompactLabels
            ? verticalReferenceLabel('Elevated HR', 'black', 'before', { verticalRatio: 1.2 })
            : referenceLineLabel('Elevated HR', 'black', { dy: -10  });
        const startLabel = isCompactLabels
            ? verticalReferenceLabel('HR Start', 'blue', 'before', { verticalRatio: 1.2})
            : referenceLineLabel('HR Start', 'blue', { dy: 10 });
        const endLabel = isCompactLabels
            ? verticalReferenceLabel('HR End', 'blue', 'after', { verticalRatio: 1.2 })
            : referenceLineLabel('HR End', 'blue', { dy: -10 });

        return (
            <div className="hrm-graph-modal-wrapper patient-task-hrm-chart">
                <div className="hrm-graph-modal-card">
                    <h5 className="hrm-graph-modal__title">
                        {formatModalTitle(hrmEvent?.totalEventDuration || 0)}
                    </h5>

                    {loading && (
                        <div className="hrm-graph-modal__loader">
                            <div className="pulse-loader-container inside-graph">
                                <PulseLoader
                                    color={"#70757a"}
                                    loading={loading}
                                    aria-label="Loading Spinner"
                                    data-testid="loader"
                                />
                            </div>
                        </div>
                    )}

                    {(!loading && chartData.length === 0) && (
                        <div className="hrm-graph-modal__empty">No heart rate data available.</div>
                    )}

                    {(!loading && chartData.length > 0) && (
                        <>
                            <div className="patient-task-hrm-chart__scroll">
                                <div className="hrm-chart-container patient-task-hrm-chart__chart">
                                <ResponsiveContainer width="100%" height={chartHeight}>
                                    <LineChart
                                        data={chartData}
                                        margin={chartMargin}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                                        <XAxis
                                            dataKey="timestamp"
                                            tickFormatter={formatTime}
                                            type="number"
                                            domain={[finalDomainMin, finalDomainMax]}
                                            scale="time"
                                            tick={{ fontSize: isMobileView ? 10 : 12 }}
                                            interval="preserveStartEnd"
                                            label={
                                                isMobileView
                                                    ? undefined
                                                    : { value: 'Time (Minutes)', position: 'bottom', offset: 0, dy: 10 }
                                            }
                                            ticks={customTicks}
                                        />

                                        <YAxis
                                            width={isMobileView ? 36 : 60}
                                            tick={{ fontSize: isMobileView ? 10 : 12 }}
                                            label={
                                                isMobileView
                                                    ? undefined
                                                    : { value: 'Heart Rate (BPM)', angle: -90, position: 'insideLeft' }
                                            }
                                        />

                                        <ChartTooltip
                                            formatter={(value) => [`${value} BPM`, 'Heart Rate']}
                                            labelFormatter={(label) => `Time: ${formatTime(label)}`}
                                        />

                                        <Legend
                                            verticalAlign="top"
                                            align="right"
                                            wrapperStyle={{
                                                width: '100%',
                                                paddingBottom: isMobileView ? 8 : 30,
                                                paddingRight: isMobileView ? 0 : 4,
                                                fontSize: isMobileView ? '11px' : '14px',
                                                color: '#555',
                                            }}
                                            iconType="circle"
                                            iconSize={8}
                                        />

                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Heart Rate"
                                            stroke="red"
                                            activeDot={{ r: isMobileView ? 4 : 6 }}
                                            dot={false}
                                            strokeWidth={isMobileView ? 2 : 3}
                                        />

                                        <ReferenceArea
                                            y1={lowThreshold}
                                            y2={highThreshold}
                                            stroke="#82ca9d"
                                            fill="#82ca9d"
                                            fillOpacity={0.2}
                                        />

                                        <ReferenceLine
                                            x={elevatedHR}
                                            stroke="black"
                                            strokeDasharray="3 3"
                                            label={elevatedLabel}
                                        />
                                        <ReferenceLine
                                            x={eventStartTime}
                                            stroke="blue"
                                            strokeDasharray="3 3"
                                            label={startLabel}
                                        />
                                        <ReferenceLine
                                            x={eventEndTime}
                                            stroke="blue"
                                            strokeDasharray="3 3"
                                            label={endLabel}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                                </div>
                            </div>
                            <p className="hrm-graph-modal__axis-label">Time(Minutes)</p>
                            <div className="hrm-graph-stats">
                                <div className="hrm-graph-stat">
                                    <span className="hrm-graph-stat__label">{"HR Below Low Threshold"}</span>
                                    <span className="hrm-graph-stat__value">{`${hrmEvent?.belowLowerThresholdCount}x <= ${hrmEvent?.hrmSensorSetting?.lowHRThreshold} bpm`}</span>
                                </div>
                                <div className="hrm-graph-stat">
                                    <span className="hrm-graph-stat__label">{"HR Above High Threshold"}</span>
                                    <span className="hrm-graph-stat__value">{`${hrmEvent?.aboveUpperThresholdCount}x >= ${hrmEvent?.hrmSensorSetting?.highHRThreshold} bpm`}</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Row className="patient-details-container">
            <NewHeartRateChart graphData={graphMomdalData} />
        </Row>
    );
};

export default HRMEventModal;
